import { Popover } from "@base-ui/react/popover"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { Controller, useForm } from "react-hook-form"

import Button from "src/components/Button"
import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Form from "src/components/Form"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"

const MAX_BODY_LENGTH = 300

const AddNotePopover = ({ onSubmit, disabled = false, submitting = false }) => {
  const [open, setOpen] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm({ defaultValues: { body: "" } })

  const closePopover = () => {
    setOpen(false)
    reset({ body: "" })
  }

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty,
    onClose: closePopover,
  })

  const handleOpenChange = (next) => {
    if (next) {
      setOpen(true)
      return
    }
    handleCloseRequest()
  }

  const onValid = ({ body }) => {
    onSubmit(body.trim(), { onSuccess: closePopover })
  }

  return (
    <>
      <Popover.Root open={open} onOpenChange={handleOpenChange} modal>
        <Popover.Trigger
          type="button"
          disabled={disabled}
          className={classNames(
            "btn btn-link btn-sm flex items-center justify-center whitespace-nowrap text-center focus:outline-none focus:ring print:hidden",
            { "opacity-50": disabled }
          )}
        >
          + Add Note
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner
            side="bottom"
            align="end"
            sideOffset={4}
            className="z-50"
          >
            <Popover.Popup
              className="w-80 rounded border bg-white p-4 shadow-lg ring-1 ring-black ring-opacity-5"
              initialFocus={false}
            >
              <Form onSubmit={handleSubmit(onValid)}>
                <Form.Label htmlFor="job_note_body">Add a note</Form.Label>
                <Controller
                  name="body"
                  control={control}
                  rules={{
                    validate: (value) =>
                      value.trim().length > 0 || "Add a note about this job.",
                  }}
                  render={({ field }) => (
                    <Form.Textarea
                      id="job_note_body"
                      rows={4}
                      maxLength={MAX_BODY_LENGTH}
                      placeholder="Add a note about this job…"
                      hasErrors={Boolean(errors.body)}
                      {...field}
                    />
                  )}
                />
                {errors.body && <Form.Error>{errors.body.message}</Form.Error>}
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    small
                    onClick={handleCloseRequest}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    small
                    disabled={!isDirty || submitting}
                  >
                    Post
                  </Button>
                </div>
              </Form>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>

      <DiscardConfirmModal {...discardConfirmProps} />
    </>
  )
}

AddNotePopover.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
}

export default AddNotePopover
